import { Injectable } from '@angular/core';

/**
 * Origin resolved for a visitor: what we send to the backend as `user.origin`.
 * Shape matches OriginI — `promoter`/`source`/`campaign` must all be non-empty or the
 * backend silently drops the origin (account.controller only saves when all three are set).
 */
export interface ResolvedOriginI {
  promoter: string;
  source: string;
  campaign: string;
  allTagsString?: string;
}

/**
 * Every tracking param we recognise. Mirrors TRACKING_PARAMS in the landing site's
 * referral-params.ts — keep the two lists in sync when adding a network.
 */
const TRACKING_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'utm_id', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic',
  'gclid', 'gbraid', 'wbraid', 'gclsrc', 'gad_source', 'gad_campaignid', 'dclid', 'srsltid',
  'msclkid',
  'fbclid', 'ttclid', 'li_fat_id', 'rdt_cid', 'qclid', 'twclid', 'tblci',
  'vmcid', 'yclid', 'ysclid',
  'ref', 'source', 'campaign',
];

/**
 * Legacy attribution snapshots (captured before the landing site started emitting `tags`)
 * only carry these camelCase fields. Mapped back to param names so old snapshots still classify.
 */
const LEGACY_ATTR_FIELDS: { [attrField: string]: string } = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmTerm: 'utm_term',
  gclid: 'gclid',
  fbclid: 'fbclid',
  ttclid: 'ttclid',
  ref: 'ref',
  source: 'source',
  campaign: 'campaign',
};

/**
 * Click-id param -> origin source. A click id is the strongest possible signal: it is minted by
 * the ad platform itself, so presence alone identifies the network with no ambiguity.
 * Ordered most-specific first; the first match wins.
 *
 * Verified against each vendor's official docs (see the landing site's TRACKING_PARAMS comment
 * for the URL of each). Notes that drove specific choices:
 *  - Google auto-tagging appends ONLY gclid — never utm_*. gbraid/wbraid replace it on iOS 14+.
 *  - Microsoft auto-tags msclkid AND utm_* (utm_source=bing, utm_medium=cpc) — the opposite
 *    asymmetry to Google, which is why bing needs both a click-id and a utm rule.
 *  - Yahoo paid search is served by Microsoft and therefore arrives as msclkid, not a Yahoo param.
 */
const CLICK_ID_SOURCES: { param: string; source: string }[] = [
  { param: 'gclid', source: 'google_ads' },
  { param: 'gbraid', source: 'google_ads' },
  { param: 'wbraid', source: 'google_ads' },
  { param: 'gclsrc', source: 'google_ads' },
  { param: 'gad_campaignid', source: 'google_ads' },
  { param: 'gad_source', source: 'google_ads' },
  { param: 'dclid', source: 'google_display_ads' },
  { param: 'srsltid', source: 'google_shopping' },
  { param: 'msclkid', source: 'microsoft_ads' },
  { param: 'fbclid', source: 'meta_ads' },
  { param: 'ttclid', source: 'tiktok_ads' },
  { param: 'li_fat_id', source: 'linkedin_ads' },
  { param: 'rdt_cid', source: 'reddit_ads' },
  { param: 'qclid', source: 'quora_ads' },
  { param: 'twclid', source: 'twitter_ads' },
  { param: 'tblci', source: 'taboola_ads' },
  { param: 'vmcid', source: 'yahoo_ads' },
  { param: 'yclid', source: 'yandex_ads' },
  { param: 'ysclid', source: 'yandex_organic' },
];

/**
 * GA4's official Paid Search medium test. Matches cpc, ppc, cpm, paid*, retargeting, ...
 * https://support.google.com/analytics/answer/9756891
 */
const PAID_MEDIUM_REGEX = /^(.*cp.*|ppc|retargeting|paid.*)$/;

/**
 * utm_source value -> { paid source, organic source }. Applied only when no click id was found.
 * `paid` is used when utm_medium looks paid (PAID_MEDIUM_REGEX), `organic` otherwise.
 *
 * Meta needs all four spellings: since Jan 2024 Meta auto-appends parameters when the advertiser
 * sets none, and it emits both long form ("Facebook"/"Instagram", per Meta's help text) and short
 * form (fb/ig, community-observed) depending on the path — so normalise defensively.
 */
const UTM_SOURCE_SOURCES: { match: RegExp; paid: string; organic: string }[] = [
  // ChatGPT is the ONLY AI assistant that tags its own outbound links, and it sets utm_source
  // only — never utm_medium/utm_campaign. Officially documented by OpenAI. The value is normally
  // "chatgpt.com", but links in the search-results "More" section were reported as plain
  // "chatgpt", so match the prefix rather than an exact value.
  { match: /^chatgpt(\.com)?$/, paid: 'chatgpt', organic: 'chatgpt' },
  { match: /^(fb|facebook)$/, paid: 'meta_ads', organic: 'facebook' },
  { match: /^(ig|instagram)$/, paid: 'meta_ads', organic: 'instagram' },
  { match: /^(bing|msn|microsoft)$/, paid: 'microsoft_ads', organic: 'bing_organic' },
  { match: /^google$/, paid: 'google_ads', organic: 'google_organic' },
  { match: /^(yahoo)$/, paid: 'yahoo_ads', organic: 'yahoo_organic' },
  { match: /^linkedin$/, paid: 'linkedin_ads', organic: 'linkedin' },
  { match: /^reddit$/, paid: 'reddit_ads', organic: 'reddit' },
  { match: /^quora$/, paid: 'quora_ads', organic: 'quora' },
  { match: /^tiktok$/, paid: 'tiktok_ads', organic: 'tiktok' },
  { match: /^(twitter|x)$/, paid: 'twitter_ads', organic: 'twitter' },
  { match: /^youtube$/, paid: 'youtube_ads', organic: 'youtube' },
];

/**
 * Referrer hostname -> origin source. The last-resort signal, used when the visitor arrived with
 * no tags at all.
 *
 * This is the ONLY way to detect Claude, Gemini, Perplexity and Copilot: none of them append any
 * URL parameters (the widely-repeated "utm_source=claude.ai"/"utm_medium=ai-assistant"
 * conventions are self-tagging advice for marketers, not platform behaviour).
 *
 * Referrer detection is a floor, not a ceiling: AI traffic from ChatGPT Atlas, the Claude desktop
 * app and in-app WebViews arrives with the referrer stripped and is indistinguishable from direct.
 * Deliberately absent: Google AI Overviews / AI Mode, whose referrer is plain google.com — there
 * is no client-side signal that separates them from ordinary organic search, and GA4 itself
 * excludes them from its AI Assistants channel.
 */
const REFERRER_SOURCES: { match: RegExp; source: string }[] = [
  // AI assistants
  { match: /(^|\.)chatgpt\.com$|(^|\.)chat\.openai\.com$/, source: 'chatgpt' },
  { match: /(^|\.)claude\.ai$/, source: 'claude' },
  { match: /(^|\.)gemini\.google\.com$/, source: 'gemini' },
  { match: /(^|\.)perplexity\.ai$/, source: 'perplexity' },
  { match: /(^|\.)copilot\.microsoft\.com$/, source: 'copilot' },
  { match: /(^|\.)deepseek\.com$/, source: 'deepseek' },
  { match: /(^|\.)grok\.com$|(^|\.)x\.ai$/, source: 'grok' },
  // Search engines
  { match: /(^|\.)google\.[a-z.]+$/, source: 'google_organic' },
  { match: /(^|\.)bing\.com$/, source: 'bing_organic' },
  // Yahoo organic often arrives via its r.search.yahoo.com redirector rather than the SERP host.
  { match: /(^|\.)search\.yahoo\.com$|(^|\.)r\.search\.yahoo\.com$/, source: 'yahoo_organic' },
  { match: /(^|\.)duckduckgo\.com$/, source: 'duckduckgo_organic' },
  { match: /(^|\.)ecosia\.org$/, source: 'ecosia_organic' },
  { match: /(^|\.)yandex\.[a-z.]+$/, source: 'yandex_organic' },
  { match: /(^|\.)baidu\.com$/, source: 'baidu_organic' },
  // Social. Meta's l./lm./m. hosts are its link shim (an interstitial that malware-checks the
  // destination), so they must bucket as facebook/instagram rather than as unknown referrers.
  { match: /(^|\.)facebook\.com$/, source: 'facebook' },
  { match: /(^|\.)instagram\.com$/, source: 'instagram' },
  { match: /(^|\.)linkedin\.com$|(^|\.)lnkd\.in$/, source: 'linkedin' },
  { match: /(^|\.)reddit\.com$/, source: 'reddit' },
  { match: /(^|\.)quora\.com$/, source: 'quora' },
  { match: /(^|\.)twitter\.com$|(^|\.)x\.com$|(^|\.)t\.co$/, source: 'twitter' },
  { match: /(^|\.)youtube\.com$|(^|\.)youtu\.be$/, source: 'youtube' },
  { match: /(^|\.)tiktok\.com$/, source: 'tiktok' },
  { match: /(^|\.)github\.com$/, source: 'github' },
  { match: /(^|\.)producthunt\.com$/, source: 'producthunt' },
];

/** Our own hosts — a referrer from these is internal navigation, not an acquisition signal. */
const SELF_REFERRER_REGEX = /(^|\.)totalum\.app$|^localhost$/;

/**
 * ⭐⭐ HOSTS A VISITOR PASSES **THROUGH**, NOT HOSTS THEY CAME **FROM**.
 *
 * ⚠️ WITHOUT THIS, `mail.google.com` WAS FILED AS `google_organic`. The
 * `(^|\.)google\.[a-z.]+$` rule above matches every Google host, so opening a
 * magic-link email in Gmail — the single most common way to reach this app —
 * counted as a Google search result. Same for `accounts.google.com` on any
 * OAuth round trip and for the Stripe Checkout return.
 *
 * ⚠️ KEEP IN SYNC with `NON_ACQUISITION_REFERRER` in
 * `totalum-platform/src/lib/attribution.ts` and `acquisitionReferrer()` in the
 * landing site's `referral-params.ts`. Three copies is two too many, but they
 * live in three deployables with no shared package.
 *
 * ⚠️ IT IS CHECKED BEFORE `REFERRER_SOURCES`, and it must stay there: the
 * organic-search rule would otherwise claim these hosts first.
 */
const NON_ACQUISITION_REFERRER_REGEX =
  /^accounts\.google\.[a-z.]+$|^(checkout|billing)\.stripe\.com$|^mail\.google\.com$|^outlook\.(live|office|office365)\.com$|^mail\.yahoo\.com$/;

const MAX_TAG_VALUE_LENGTH = 300;
const MAX_ALL_TAGS_STRING_LENGTH = 2000;
const MAX_SOURCE_LENGTH = 60;
const MAX_CAMPAIGN_LENGTH = 200;

/**
 * Works out where a visitor came from, from the tracking params on the URL, the first-touch
 * `attr` snapshot carried over from the landing site, and the referrer.
 *
 * Used by every page that captures origin (register flow + magic-link login) so the precedence
 * rules live in exactly one place.
 */
@Injectable({ providedIn: 'root' })
export class OriginDetectionService {

  /**
   * Resolve the visitor's origin, or null when it genuinely cannot be determined (no tags, no
   * usable referrer) — null is what makes the register form fall back to asking the user.
   *
   * Precedence, strongest evidence first:
   *  1. `ref`     — an affiliate referral. Money is attached to it, so it always wins.
   *  2. tags      — a click id or utm_*. A real campaign beats the soft touches below.
   *  3. `source`  — the landing site's own soft touch (source=blog / sublanding).
   *  4. referrer  — the only signal most AI assistants give us.
   */
  resolve(params: { [key: string]: any }, attr?: string | null, documentReferrer?: string): ResolvedOriginI | null {
    const attrData = this.decodeAttr(attr);
    const tags = this.collectTags(params, attrData);
    const referrer = this.resolveReferrer(attrData, documentReferrer);
    const allTagsString = this.buildAllTagsString(tags, referrer);

    // 1. Affiliate referral. Preserves the original shape exactly: a bare ?ref= yields
    //    source=referral / campaign=affiliate, and an explicit source/campaign overrides those.
    if (tags['ref']) {
      return {
        promoter: tags['ref'],
        source: tags['source'] || 'referral',
        campaign: tags['campaign'] || 'affiliate',
        allTagsString,
      };
    }

    // 2. Detected campaign (click id or utm_*).
    const detected = this.detectFromTags(tags);
    if (detected) {
      return { promoter: 'organic', ...detected, allTagsString };
    }

    // 3. The landing site's soft touch (source=blog + campaign=<path>, or a hand-built link).
    if (tags['source'] || tags['campaign']) {
      return {
        promoter: 'organic',
        source: tags['source'] || 'direct',
        campaign: tags['campaign'] || 'none',
        allTagsString,
      };
    }

    // 4. Referrer — AI assistants, organic search, social.
    const fromReferrer = this.detectFromReferrer(referrer);
    if (fromReferrer) {
      return { promoter: 'organic', ...fromReferrer, allTagsString };
    }

    return null;
  }

  /**
   * Classify from tracking params. Click ids first (a platform-minted id is unambiguous), then
   * utm_source, then a generic fallback so an unrecognised campaign is still recorded rather
   * than thrown away.
   */
  private detectFromTags(tags: { [key: string]: string }): { source: string; campaign: string } | null {
    const campaign = this.resolveCampaign(tags);

    for (const clickId of CLICK_ID_SOURCES) {
      if (tags[clickId.param]) {
        return { source: clickId.source, campaign };
      }
    }

    const utmSource = (tags['utm_source'] || '').toLowerCase();
    if (utmSource) {
      const isPaid = PAID_MEDIUM_REGEX.test((tags['utm_medium'] || '').toLowerCase());
      for (const rule of UTM_SOURCE_SOURCES) {
        if (rule.match.test(utmSource)) {
          return { source: isPaid ? rule.paid : rule.organic, campaign };
        }
      }
      // Generic: an unrecognised utm_source is still a deliberate tag — keep it verbatim.
      return { source: this.slugify(utmSource), campaign };
    }

    // GA4's all-or-nothing rule means a campaign can arrive with no utm_source at all. Record it
    // under a generic source rather than losing the campaign entirely.
    if (tags['utm_campaign'] || tags['utm_medium'] || tags['utm_id']) {
      return { source: this.slugify(tags['utm_medium'] || 'other'), campaign };
    }

    return null;
  }

  /** Classify from a referrer hostname. */
  private detectFromReferrer(referrer?: string): { source: string; campaign: string } | null {
    const host = this.hostnameOf(referrer);
    if (!host || SELF_REFERRER_REGEX.test(host) || NON_ACQUISITION_REFERRER_REGEX.test(host)) {
      return null;
    }

    for (const rule of REFERRER_SOURCES) {
      if (rule.match.test(host)) {
        return { source: rule.source, campaign: 'none' };
      }
    }

    // Unknown external site: record the host so the referral is still attributable.
    return { source: this.slugify(host.replace(/^www\./, '')), campaign: 'referral' };
  }

  /**
   * Campaign name for a tag-detected origin. gad_campaignid is Google's own campaign identifier
   * and is the best fallback when the advertiser did not tag utm_campaign.
   *
   * Deliberately ignores our own `campaign` param: for a soft touch it holds the blog/sublanding
   * path, so consulting it here would label an ad click with a campaign of "/blog/how-to-x".
   * The soft-touch branch in resolve() reads that param directly instead.
   */
  private resolveCampaign(tags: { [key: string]: string }): string {
    const campaign = tags['utm_campaign'] || tags['gad_campaignid'] || tags['utm_id'];
    return campaign ? campaign.slice(0, MAX_CAMPAIGN_LENGTH) : 'none';
  }

  /**
   * Merge tracking params from the current URL with the first-touch `attr` snapshot.
   * The live URL wins: it describes the click that is happening now, whereas `attr` may have
   * been captured on an earlier visit.
   */
  private collectTags(params: { [key: string]: any }, attrData: { [key: string]: any }): { [key: string]: string } {
    const tags: { [key: string]: string } = {};

    // Oldest evidence first, so newer writes overwrite it.
    for (const attrField of Object.keys(LEGACY_ATTR_FIELDS)) {
      const value = attrData[attrField];
      if (typeof value === 'string' && value.trim()) {
        tags[LEGACY_ATTR_FIELDS[attrField]] = value.trim().slice(0, MAX_TAG_VALUE_LENGTH);
      }
    }

    const attrTags = attrData['tags'];
    if (attrTags && typeof attrTags === 'object') {
      for (const key of TRACKING_PARAMS) {
        const value = attrTags[key];
        if (typeof value === 'string' && value.trim()) {
          tags[key] = value.trim().slice(0, MAX_TAG_VALUE_LENGTH);
        }
      }
    }

    for (const key of TRACKING_PARAMS) {
      const value = params ? params[key] : undefined;
      if (typeof value === 'string' && value.trim()) {
        tags[key] = value.trim().slice(0, MAX_TAG_VALUE_LENGTH);
      }
    }

    return tags;
  }

  /**
   * Serialise every tag we found, plus the referrer, as one query-style string for BI.
   * Values keep their original case — click ids such as gclid/gbraid are case-sensitive and
   * lower-casing them would break conversion attribution.
   */
  private buildAllTagsString(tags: { [key: string]: string }, referrer?: string): string | undefined {
    const parts: string[] = [];

    for (const key of TRACKING_PARAMS) {
      if (tags[key]) {
        parts.push(`${key}=${encodeURIComponent(tags[key])}`);
      }
    }
    if (referrer) {
      parts.push(`referrer=${encodeURIComponent(referrer.slice(0, MAX_TAG_VALUE_LENGTH))}`);
    }

    if (!parts.length) {
      return undefined;
    }
    return parts.join('&').slice(0, MAX_ALL_TAGS_STRING_LENGTH);
  }

  /**
   * The first-touch referrer from the landing snapshot, falling back to this page's referrer for
   * visitors who came straight to the accounts app (e.g. an ad pointing at /login).
   */
  private resolveReferrer(attrData: { [key: string]: any }, documentReferrer?: string): string | undefined {
    const fromAttr = attrData['referrer'];
    if (typeof fromAttr === 'string' && fromAttr.trim()) {
      return fromAttr.trim();
    }
    return documentReferrer && documentReferrer.trim() ? documentReferrer.trim() : undefined;
  }

  /** Decode base64(encodeURIComponent(JSON)) — mirror of the landing site's encoder. */
  private decodeAttr(attr?: string | null): { [key: string]: any } {
    if (!attr) {
      return {};
    }
    try {
      const parsed = JSON.parse(decodeURIComponent(atob(attr)));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private hostnameOf(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }
    try {
      return new URL(url).hostname.toLowerCase();
    } catch {
      return undefined;
    }
  }

  private slugify(value: string): string {
    return value.toLowerCase().trim().replace(/[^a-z0-9._-]+/g, '_').slice(0, MAX_SOURCE_LENGTH) || 'other';
  }
}
